"use client";

import React from "react";
import RegisterForm from "@/components/auth/RegisterForm";
import AuthIllustrationPanel from "@/components/auth/AuthIllustrationPanel";
import type { PageUrlMap } from "@/types/blog";
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";

interface Agreement {
  id: number;
  title: string;
  slug: string;
  content?: string;
  is_required: boolean;
}

interface RegisterPageProps {
  language?: string;
  languageId?: number;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  agreements?: Agreement[];
  socialProviders?: Array<{ provider: string; isActive: boolean; name?: string }>;
  requireCaptcha?: boolean;
  formTitle?: string;
  __grapesPreview?: boolean;
}

export default function RegisterPage({
  language = 'en',
  languageId,
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  agreements = [],
  socialProviders = [],
  requireCaptcha = true,
  formTitle,
  __grapesPreview = false,
  ...restProps
}: RegisterPageProps) {
  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/RegisterPage',
    { language, translations, pageUrls, agreements, socialProviders, requireCaptcha, formTitle },
    isBackendRender
  );
  const { getVariable } = useLanguageVariables();

  const t = (key: string, fallback: string) => {
    return translations?.[key] || getVariable(key, fallback);
  };

  return (
    <div {...backendAttributes} className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen">
        <AuthIllustrationPanel variant="register" />

        <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t("auth.register.title", formTitle || "Create your account")}
              </h1>
              <p className="text-gray-600">
                {t("auth.register.subtitle", "Sign up to get started")}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8">
              <RegisterForm
                language={language}
                languageId={languageId}
                defaultLanguage={defaultLanguage}
                translations={translations}
                pageUrls={pageUrls}
                agreements={agreements}
                socialProviders={socialProviders}
                requireCaptcha={requireCaptcha}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
